import { CommonModule, Location } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { Store } from '@ngxs/store';
import { first, catchError, map, switchMap } from 'rxjs/operators';
import { RegistrationService } from 'src/app/registration/shared/registration.service';
import { RegistrationState, NotificationStateModel } from 'src/app/registration/state/registration.state';
import { DateTimeService } from 'src/app/shared/date-time-convertor/date-time.service';
import { DriverApiService } from 'src/app/shared/services/driver-api.service';
import { ToastService } from 'src/app/shared/toast/toast.service';
import { SubSink } from 'subsink';
import { DQF_DOC_STATUS, POLICE_CLEARANCE, DQF_SUBMITTED_STATUSES } from '../constants';
import { DriverQualificationService } from '../service/driver-qualification.service';
import { DRIVER_DOC_CONTEXT_INITIAL_DQF } from '../constants';
import { ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, ROUTE_DQF_REVIEW } from 'src/app/shared/routes';
import { Router } from '@angular/router';
import { of } from 'rxjs';
import { ComponentsModule } from 'src/app/shared/components/components.module';
import { DqfDocumentSkeletonComponent } from '../dqf-document-skeleton/dqf-document-skeleton.component';

const REPORT_TYPE = 'PoliceClearance';
const UPLOADED_STATUS = 'uploaded';
const UPLOADED_DOCUMENT = 'Successfully uploaded document';
const DELETE_MESSAGE = 'Are you sure you want to delete this document? This action cannot be undone.';
const DELETED_DOCUMENT = 'Document deleted successfully';
const REQUEST_SUCESS = 'Successfully sent the request';
const UNDO_REQUEST_SUCCESS = 'Successfully cancelled the request';

const DISABLE_TOP_LOADING_BAR = true;

@Component({
    selector: 'app-dqf-police-certificate',
    templateUrl: './dqf-police-certificate.component.html',
    styleUrls: ['./dqf-police-certificate.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        MatIconModule,
        ReactiveFormsModule,
        ComponentsModule,
        DqfDocumentSkeletonComponent
    ]
})
export class DqfPoliceCertificateComponent implements OnInit {
  form!: UntypedFormGroup;
  formUploadFiles: { form: UntypedFormGroup | null } = { form: null };
  fileInfo!: { id: string; fileName: string; generationDate: string };
  subSink = new SubSink();
  isContentReady: boolean = false;
  documentStatus!: string;
  documentType = POLICE_CLEARANCE;
  constructor(
    private readonly location: Location,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly driverQualificationService: DriverQualificationService,
    private readonly driverApiService: DriverApiService,
    private readonly dateTimeService: DateTimeService,
    private readonly toastService: ToastService,
    private readonly registrationService: RegistrationService,
    private readonly store: Store,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.form = this.createForm();
    this.formUploadFiles = {
      form: this.form
    };
    this.loadPageData();
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  backAction(): void {
    this.location.back();
  }
  get canUndoCarrierRequest(): boolean {
    return !DQF_SUBMITTED_STATUSES.includes(this.driverQualificationService.dqfData?.status);
  }

  getDocumentDqfStatus(): void {
    const document = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(REPORT_TYPE);
    if (!document) {
      return;
    }

    this.documentStatus = document.status;
  }

  private shouldBindUploadedDocument(): boolean {
    const document = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(REPORT_TYPE);
    if (!document) {
      return false;
    }

    // After carrier deletion the attached doc is Pending with incomplete requirements.
    if (
      document.status === DQF_DOC_STATUS.PENDING ||
      document.status === DQF_DOC_STATUS.REJECTED
    ) {
      return false;
    }

    // Match list UI: driver-submitted / accepted PCC should show the uploaded file.
    if (
      document.status === DQF_DOC_STATUS.DRIVER_REVIEWED ||
      document.status === DQF_DOC_STATUS.DRIVER_SIGNED ||
      document.status === DQF_DOC_STATUS.ACCEPTED
    ) {
      return true;
    }

    return document.isRequirementsCompleted === true;
  }

  private loadPageData(): void {
    this.isContentReady = false;
    this.subSink.sink = this.driverQualificationService
      .getDqfById(this.driverQualificationService.dqfId)
      .pipe(
        switchMap(() =>
          this.registrationService.loadDriver(DISABLE_TOP_LOADING_BAR).pipe(
            map(() => this.store.selectSnapshot(RegistrationState.notification))
          )
        ),
        catchError((error: any) => {
          console.error('Error loading police clearance page data:', error);
          this.isContentReady = true;
          return of(null);
        })
      )
      .subscribe((notificationStateModel: NotificationStateModel | null) => {
        this.getDocumentDqfStatus();
        this.isContentReady = true;
        // Bind after upload-document mounts; it only watches valueChanges and would miss an earlier patch.
        setTimeout(() => this.bindDriverDocumentIfPresent(notificationStateModel));
      });
  }

  private bindDriverDocumentIfPresent(notificationStateModel: NotificationStateModel | null): void {
    if (!this.shouldBindUploadedDocument()) {
      this.form.reset();
      this.formUploadFiles.form?.get('file')?.patchValue(null);
      return;
    }

    if (!notificationStateModel?.notifications) {
      return;
    }

    const attachedDocument = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(REPORT_TYPE);
    const matchingNotifications = notificationStateModel.notifications.filter(
      notification =>
        notification.type === REPORT_TYPE &&
        notification.status == UPLOADED_STATUS &&
        notification?.dqfId === this.driverQualificationService.dqfId
    );

    if (!matchingNotifications.length) {
      return;
    }

    // Prefer the notification linked on the DQF attached document, otherwise use the latest upload.
    const attachedDocumentId = (attachedDocument as { id?: string } | undefined)?.id;
    const notificationToBind =
      matchingNotifications.find(notification => notification.id === attachedDocumentId) ||
      [...matchingNotifications].sort(
        (a, b) => new Date(b.createdDate || 0).getTime() - new Date(a.createdDate || 0).getTime()
      )[0];

    this.driverQualificationService.bindDriverDocument.call(this, notificationToBind, this.formUploadFiles);
  }
  uploadDocument(): void {
    this.isContentReady = false;
    const model = this.driverQualificationService.prepareUploadDocumentSaveModel(
      this.form.value,
      REPORT_TYPE,
      DRIVER_DOC_CONTEXT_INITIAL_DQF,
      this.driverQualificationService.dqfId
    );
    if (model && this.formUploadFiles.form?.valid) {
      this.subSink.sink = this.driverQualificationService
        .uploadDqfDocument(model, this.form.value.file, DISABLE_TOP_LOADING_BAR)
        .pipe(
          first(),
          catchError((error: any) => {
            this.isContentReady = true;
            this.formUploadFiles.form?.get('file')?.patchValue(null);
            this.formUploadFiles = { ...this.formUploadFiles };
            this.toastService.showError(error?.message || 'Failed to upload document. Please try again.');

            return of(false);
          })
        )
        .subscribe(result => {
          if (!result) {
            return;
          }

          // Document file is already saved. Only transition DQF status when needed.
          // Re-applying DriverReviewed on an already-reviewed PCC returns API 4007.
          if (this.isPoliceClearanceAlreadyReviewed()) {
            this.toastService.showSuccess(UPLOADED_DOCUMENT);
            this.navigateBackToDqfDocuments();
            return;
          }

          const docModel = {
            attachedDocument: {
              type: REPORT_TYPE,
              status: DQF_DOC_STATUS.DRIVER_REVIEWED,
              uploadType: 'file'
            }
          };
          this.updateDqfDocStatus(docModel, UPLOADED_DOCUMENT);
        });
    } else {
      this.isContentReady = true;
    }
  }
  requestCarrierToUploadDocument(): void {
    const docModel = {
      attachedDocument: {
        type: REPORT_TYPE,
        status: DQF_DOC_STATUS.CARRIER_UPLOAD_REQUESTED
      }
    };
    this.updateDqfDocStatus(docModel, REQUEST_SUCESS);
  }

  undoRequestCarrierToUploadDocument(): void {
    if (!this.canUndoCarrierRequest) {
      return;
    }
    const docModel = {
      attachedDocument: {
        type: REPORT_TYPE,
        status: DQF_DOC_STATUS.PENDING
      }
    };
    this.updateDqfDocStatus(docModel, UNDO_REQUEST_SUCCESS);
  }

  deleteDocument(): void {
    if (confirm(DELETE_MESSAGE)) {
      if (this.formUploadFiles.form?.value?.file?.id) {
        this.isContentReady = false;
        this.subSink.sink = this.driverQualificationService
          .deleteDqfDocument(this.formUploadFiles.form?.value?.file?.id, DISABLE_TOP_LOADING_BAR)
          .subscribe(
            () => {
              this.isContentReady = true;
              this.toastService.showSuccess(DELETED_DOCUMENT);
              this.form.reset();
              this.formUploadFiles.form?.get('file')?.patchValue(null);
            },
            error => {
              this.isContentReady = true;
              return of(false);
            }
          );
      }
    }
  }

  updateDqfDocStatus(docModel: any, successMessage: string): void {
    const dqfId = this.driverQualificationService.dqfId;
    this.subSink.sink = this.driverQualificationService
      .updateDqfStatus(dqfId, docModel)
      .pipe(
        switchMap(() => {
          this.syncAttachedDocument(docModel?.attachedDocument);
          return dqfId ? this.driverQualificationService.getDqfById(dqfId) : of(null);
        })
      )
      .subscribe({
        next: dqf => {
          if (dqf) {
            this.driverQualificationService.dqfData = dqf;
          }
          this.toastService.showSuccess(successMessage);
          this.navigateBackToDqfDocuments();
        },
        error: (error: any) => {
          this.isContentReady = true;
          this.toastService.showError(
            error?.message || 'Unable to update Criminal Record (Background) Check status. Please try again.'
          );
        }
      });
  }

  private isPoliceClearanceAlreadyReviewed(): boolean {
    const document = this.driverQualificationService.getDqfDocumentFromAttachedDocuments(REPORT_TYPE);
    return (
      document?.status === DQF_DOC_STATUS.DRIVER_REVIEWED ||
      document?.status === DQF_DOC_STATUS.DRIVER_SIGNED ||
      document?.status === DQF_DOC_STATUS.ACCEPTED
    );
  }

  private syncAttachedDocument(attachedDocument: { type?: string; status?: string }): void {
    const document = this.driverQualificationService.dqfData?.attachedDocuments?.find(
      doc => doc.type === attachedDocument?.type
    );
    if (!document || !attachedDocument?.status) {
      return;
    }

    document.status = attachedDocument.status;
    if (attachedDocument.status === DQF_DOC_STATUS.PENDING) {
      document.isRequirementsCompleted = false;
    }
  }

  navigateBackToDqfDocuments(): void {
    const dqfId = this.driverQualificationService.dqfId;
    if (!dqfId) {
      this.router.navigate([ROUTE_DQF]);
      return;
    }

    if (DQF_SUBMITTED_STATUSES.includes(this.driverQualificationService.dqfData?.status)) {
      this.router.navigate([ROUTE_DQF, ROUTE_DQF_REVIEW, dqfId]);
      return;
    }

    this.router.navigate([ROUTE_DQF, ROUTE_DQF_DOCUMENT_UPLOAD, dqfId]);
  }

  private createForm(): UntypedFormGroup {
    return this.formBuilder.group({
      generationDate: ['', Validators.required],
      file: ['', Validators.required]
    });
  }
}
